/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import BioIndexQuizNavigation from "../components/BioIndexQuizNavigation";
import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexQuiz.css";
import {
  generaByFamily,
  familiesByOrder,
  ordersByClass,
  classesByPhylum,
  phylaByKingdom,
} from "../utils/taxonomy";

const quizTypeLabels = {
  "scientific-from-photo": "Scientific name from photo",
  "common-from-photo": "Common name from photo",
  "genus-from-photo": "Genus from photo",
  "family-from-photo": "Family from photo",
  "photo-from-scientific": "Photo from scientific name",
  "photo-from-common": "Photo from common name",
};

export default function QuizPage() {
  const location = useLocation();
  const {
    quizType: initialQuizType,
    filters,
    questionCount,
    sourceUID,
  } = location.state || {};
  const [imageLoaded, setImageLoaded] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selected, setSelected] = useState(null);
  const [answerResults, setAnswerResults] = useState([]);
  const [quizLoaded, setQuizLoaded] = useState(false);

  const [quizPool, setQuizPool] = useState([]);
  const [activeQuizType, setActiveQuizType] = useState(
    initialQuizType || "scientific-from-photo",
  );
  const [setupQuizType, setSetupQuizType] = useState(
    initialQuizType || "scientific-from-photo",
  );
  const [setupQuestionCount, setSetupQuestionCount] = useState(
    questionCount || 10,
  );

  useEffect(() => {
    setImageLoaded(false);
  }, [current]);

  useEffect(() => {
    questions.forEach((question) => {
      if (question.photo) {
        const img = new Image();
        img.src = question.photo;
      }

      if (activeQuizType?.includes("photo-from")) {
        (question.options || []).forEach((url) => {
          const img = new Image();
          img.src = url;
        });
      }
    });
  }, [questions, activeQuizType]);

  useEffect(() => {
    async function load() {
      const uid = sourceUID || auth.currentUser?.uid;

      if (!uid) {
        setQuestions([]);
        setQuizLoaded(true);
        return;
      }

      const snap = await getDocs(collection(db, "users", uid, "species"));

      const list = [];

      snap.forEach((docSnap) => {
        const data = docSnap.data();

        if (!data.forms || data.forms.length === 0) return;

        const genus = String(data.genus || "")
          .toLowerCase()
          .trim();

        const family = Object.keys(generaByFamily).find((f) =>
          (generaByFamily[f] || []).some(
            (g) => String(g).toLowerCase().trim() === genus,
          ),
        );

        const order = Object.keys(familiesByOrder).find((o) =>
          (familiesByOrder[o] || []).some(
            (f) =>
              String(f).toLowerCase().trim() ===
              String(family || "")
                .toLowerCase()
                .trim(),
          ),
        );

        const className = Object.keys(ordersByClass).find((c) =>
          (ordersByClass[c] || []).some(
            (o) =>
              String(o).toLowerCase().trim() ===
              String(order || "")
                .toLowerCase()
                .trim(),
          ),
        );

        const phylum = Object.keys(classesByPhylum).find((p) =>
          (classesByPhylum[p] || []).some(
            (c) =>
              String(c).toLowerCase().trim() ===
              String(className || "")
                .toLowerCase()
                .trim(),
          ),
        );

        const kingdom = Object.keys(phylaByKingdom).find((k) =>
          (phylaByKingdom[k] || []).some(
            (p) =>
              String(p).toLowerCase().trim() ===
              String(phylum || "")
                .toLowerCase()
                .trim(),
          ),
        );

        list.push({
          id: docSnap.id,
          name: data.name,
          common: data.common || "",
          genus: data.genus,
          forms: data.forms,
          family,
          order,
          className,
          phylum,
          kingdom,
        });
      });

      const filtered = applyFilters(list);
      setQuizPool(filtered);

      const quizQuestions = generateQuestions(
        filtered,
        activeQuizType,
        Math.min(questionCount || 10, filtered.length),
      );

      setQuestions(quizQuestions);
      setQuizLoaded(true);
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyFilters(list) {
    if (!filters) return list;

    return list.filter((sp) => {
      if (filters.collection) {
        const hasCollection = (sp.forms || []).some((f) =>
          (f.collections || []).includes(filters.collection),
        );
        if (!hasCollection) return false;
      }

      const genus = String(sp.genus || "")
        .toLowerCase()
        .trim();
      const family = String(sp.family || "")
        .toLowerCase()
        .trim();
      const order = String(sp.order || "")
        .toLowerCase()
        .trim();
      const className = String(sp.className || "")
        .toLowerCase()
        .trim();
      const phylum = String(sp.phylum || "")
        .toLowerCase()
        .trim();
      const kingdom = String(sp.kingdom || "")
        .toLowerCase()
        .trim();

      if (filters.genus && genus !== String(filters.genus).toLowerCase().trim())
        return false;

      if (
        filters.family &&
        family !== String(filters.family).toLowerCase().trim()
      )
        return false;

      if (filters.order && order !== String(filters.order).toLowerCase().trim())
        return false;

      if (
        filters.class &&
        className !== String(filters.class).toLowerCase().trim()
      )
        return false;

      if (
        filters.phylum &&
        phylum !== String(filters.phylum).toLowerCase().trim()
      )
        return false;

      if (
        filters.kingdom &&
        kingdom !== String(filters.kingdom).toLowerCase().trim()
      )
        return false;

      return true;
    });
  }

  function randomSample(arr, n) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, n);
  }

  function shuffle(arr) {
    return randomSample(arr, arr.length);
  }

  function generateQuestions(
    speciesList,
    requestedQuizType = activeQuizType,
    requestedQuestionCount = questionCount || 10,
  ) {
    const selectedSpecies = randomSample(speciesList, requestedQuestionCount);
    const photoPool = speciesList.flatMap((s) => s.forms.map((f) => f.url));

    return selectedSpecies.map((sp) => {
      const photo = sp.forms[Math.floor(Math.random() * sp.forms.length)].url;

      let correct;
      let options;

      if (requestedQuizType === "scientific-from-photo") {
        correct = sp.name;
        options = shuffle([
          correct,
          ...randomSample(
            speciesList.filter((s) => s.name !== correct).map((s) => s.name),
            3,
          ),
        ]);
      }

      if (requestedQuizType === "common-from-photo") {
        correct = sp.common || sp.name;
        options = shuffle([
          correct,
          ...randomSample(
            speciesList
              .filter((s) => (s.common || s.name) !== correct)
              .map((s) => s.common || s.name),
            3,
          ),
        ]);
      }

      if (requestedQuizType === "genus-from-photo") {
        correct = sp.genus;
        options = shuffle([
          correct,
          ...randomSample(
            speciesList.filter((s) => s.genus !== correct).map((s) => s.genus),
            3,
          ),
        ]);
      }

      if (requestedQuizType === "family-from-photo") {
        const fam = Object.keys(generaByFamily).find((f) =>
          generaByFamily[f].some(
            (g) =>
              String(g).toLowerCase().trim() ===
              String(sp.genus).toLowerCase().trim(),
          ),
        );

        const format = (s) =>
          String(s || "")
            .charAt(0)
            .toUpperCase() +
          String(s || "")
            .slice(1)
            .toLowerCase();

        correct = format(fam);

        const allFamilies = Object.keys(generaByFamily);

        options = shuffle([
          correct,
          ...randomSample(
            allFamilies
              .filter(
                (f) =>
                  String(f).toLowerCase().trim() !==
                  String(fam).toLowerCase().trim(),
              )
              .map(format),
            3,
          ),
        ]);
      }

      if (
        requestedQuizType === "photo-from-scientific" ||
        requestedQuizType === "photo-from-common"
      ) {
        correct = photo;

        options = shuffle([
          correct,
          ...randomSample(
            photoPool.filter((url) => url !== correct),
            3,
          ),
        ]);
      }

      return {
        species: sp,
        photo,
        correct,
        options,
      };
    });
  }

  function selectAnswer(option) {
    if (answered) return;
    setSelected(option);
  }

  function submitAnswer() {
    if (answered || selected === null) return;

    setAnswered(true);

    const isCorrect = selected === questions[current].correct;

    setAnswerResults((prev) => {
      const next = [...prev];
      next[current] = isCorrect;
      return next;
    });

    if (isCorrect) {
      setScore((prev) => prev + 1);
    }
  }

  function nextQuestion() {
    setAnswered(false);
    setSelected(null);
    setCurrent(current + 1);
  }

  function startNewQuizInPlace() {
    if (!quizPool.length) return;

    const safeQuestionCount = Math.min(setupQuestionCount, quizPool.length);

    setActiveQuizType(setupQuizType);
    setQuestions(generateQuestions(quizPool, setupQuizType, safeQuestionCount));
    setCurrent(0);
    setScore(0);
    setAnswered(false);
    setSelected(null);
    setAnswerResults([]);
  }

  function formatQuizDate(dateVal) {
    if (!dateVal || typeof dateVal !== "object") return "";

    const year = dateVal.year;
    const month = dateVal.month;
    const day = dateVal.day;

    if (!year || year === "NA") return "";

    const monthNames = {
      "01": "January",
      "02": "February",
      "03": "March",
      "04": "April",
      "05": "May",
      "06": "June",
      "07": "July",
      "08": "August",
      "09": "September",
      10: "October",
      11: "November",
      12: "December",
    };

    if (!month || month === "NA") return String(year);

    const monthName = monthNames[month] || month;

    if (!day || day === "NA") return `${monthName} ${year}`;

    return `${String(Number(day))} ${monthName} ${year}`;
  }

  function buildFeedbackText() {
    const isCorrect = selected === q.correct;
    const form = (q.species.forms || []).find((f) => f.url === q.photo) || {};
    const scientific = q.species.name;
    const common = q.species.common;
    const family = q.species.family
      ? String(q.species.family).charAt(0).toUpperCase() +
        String(q.species.family).slice(1).toLowerCase()
      : "";
    const location = form.locationName || form.location || "";
    const date = formatQuizDate(form.date);

    let text =
      activeQuizType === "common-from-photo"
        ? isCorrect
          ? `Correct! This is a ${common || scientific}`
          : `Not quite. This is a ${common || scientific}`
        : isCorrect
          ? `Correct! This is ${scientific}`
          : `Not quite. This is ${scientific}`;

    if (activeQuizType === "common-from-photo" && common)
      text += `, also known as ${scientific},`;

    if (activeQuizType !== "common-from-photo" && common)
      text += `, also known as ${common},`;
    if (family) text += ` from the ${family} family`;

    if (location && date)
      text += `. This photograph was taken in ${location} on ${date}`;
    else if (location) text += `. This photograph was taken in ${location}`;
    else if (date) text += `. This photograph was taken on ${date}`;

    return `${text}.`;
  }

  function getSummaryName(question) {
    if (activeQuizType === "common-from-photo")
      return question.species.common || question.species.name;

    if (activeQuizType === "genus-from-photo") return question.species.genus;

    if (activeQuizType === "family-from-photo") return question.correct;

    return question.species.name;
  }

  const quizOwnerUID = sourceUID || auth.currentUser?.uid;

  if (!quizLoaded) {
    return (
      <div className="bioindex-quiz-page">
        <BioIndexQuizNavigation sourceUID={quizOwnerUID} />
        <main className="bioindex-quiz-container">
          <section className="bioindex-quiz-empty" role="status">
            <div className="bioindex-quiz-loading-spinner" aria-hidden="true" />
            <h1>Preparing your quiz</h1>
          </section>
        </main>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="bioindex-quiz-page">
        <BioIndexQuizNavigation sourceUID={quizOwnerUID} />
        <main className="bioindex-quiz-container">
          <section className="bioindex-quiz-empty">
            <h1>No questions available</h1>
            <p>No valid quiz questions were found for this selection.</p>
            <Link className="bioindex-quiz-primary" to="/profile">
              Return to Profile
            </Link>
          </section>
        </main>
      </div>
    );
  }

  if (current >= questions.length) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="bioindex-quiz-page">
        <BioIndexQuizNavigation sourceUID={quizOwnerUID} />
        <main className="bioindex-quiz-container">
          <header className="bioindex-quiz-results-header">
            <p className="bioindex-quiz-overline">
              BioIndex · Taxonomy Challenge
            </p>
            <h1>Quiz Complete</h1>
            <div className="bioindex-quiz-score-display">
              <strong>{percentage}%</strong>
              <span>
                {score} of {questions.length} correct
              </span>
            </div>
          </header>

          <div className="bioindex-quiz-results-grid">
            <section
              className="bioindex-quiz-card"
              aria-labelledby="quiz-summary-heading"
            >
              <div className="bioindex-quiz-section-heading">
                <h2 id="quiz-summary-heading">Your answers</h2>
                <span>{quizTypeLabels[activeQuizType]}</span>
              </div>
              <div className="bioindex-quiz-summary-grid">
                {questions.map((question, index) => {
                  const wasCorrect = answerResults[index] === true;
                  return (
                    <article
                      className={`bioindex-quiz-summary-item${wasCorrect ? " is-correct" : " is-wrong"}`}
                      key={`${question.photo}-${index}-summary`}
                    >
                      <img src={question.photo} alt="" />
                      <div className="bioindex-quiz-summary-copy">
                        <span>Question {index + 1}</span>
                        <strong
                          className={
                            activeQuizType === "scientific-from-photo" ||
                            activeQuizType === "photo-from-scientific"
                              ? "bioindex-quiz-scientific"
                              : ""
                          }
                        >
                          {getSummaryName(question)}
                        </strong>
                        {question.species.common &&
                          getSummaryName(question) !==
                            question.species.common && (
                            <small>{question.species.common}</small>
                          )}
                      </div>
                      <span
                        className="bioindex-quiz-summary-result"
                        aria-label={wasCorrect ? "Correct" : "Incorrect"}
                      >
                        {wasCorrect ? "✓" : "×"}
                      </span>
                    </article>
                  );
                })}
              </div>
            </section>
            <aside
              className="bioindex-quiz-card bioindex-quiz-again-card"
              aria-labelledby="quiz-again-heading"
            >
              <div className="bioindex-quiz-section-heading">
                <h2 id="quiz-again-heading">Another round</h2>
              </div>
              <div className="bioindex-quiz-field">
                <label htmlFor="quiz-again-type">Question type</label>
                <select
                  id="quiz-again-type"
                  value={setupQuizType}
                  onChange={(event) => setSetupQuizType(event.target.value)}
                >
                  {Object.entries(quizTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="bioindex-quiz-field">
                <label htmlFor="quiz-again-count">Questions</label>
                <input
                  id="quiz-again-count"
                  type="number"
                  min="1"
                  max={quizPool.length || 1}
                  value={setupQuestionCount}
                  onChange={(event) =>
                    setSetupQuestionCount(
                      Math.min(
                        quizPool.length || 1,
                        Math.max(1, Number(event.target.value) || 1),
                      ),
                    )
                  }
                />
              </div>
              <button
                type="button"
                onClick={startNewQuizInPlace}
                disabled={!quizPool.length}
                className="bioindex-quiz-primary"
              >
                Start New Quiz
              </button>
              <Link to="/profile" className="bioindex-quiz-secondary">
                Return to Profile
              </Link>
            </aside>
          </div>
        </main>
      </div>
    );
  }

  const q = questions[current];
  const isPhotoChoiceQuiz = activeQuizType?.includes("photo-from");
  const progressPercent = ((current + 1) / questions.length) * 100;
  const quizTypeLabel = quizTypeLabels[activeQuizType] || "BioIndex Quiz";
  const questionPrompt =
    activeQuizType === "scientific-from-photo"
      ? "What is the scientific name of this species?"
      : activeQuizType === "common-from-photo"
        ? "What is the common name of this species?"
        : activeQuizType === "genus-from-photo"
          ? "What genus does this species belong to?"
          : activeQuizType === "family-from-photo"
            ? "What family does this species belong to?"
            : activeQuizType === "photo-from-scientific"
              ? "Which photo matches this scientific name?"
              : "Which photo matches this common name?";

  const feedbackText = answered ? buildFeedbackText() : "";
  const scientificIndex = feedbackText.indexOf(q.species.name || "");
  const feedback = answered ? (
    <div
      className={`bioindex-quiz-feedback${selected === q.correct ? " is-correct" : " is-wrong"}`}
      role="status"
    >
      <span className="bioindex-quiz-feedback-symbol" aria-hidden="true">
        {selected === q.correct ? "✓" : "×"}
      </span>
      <p>
        {scientificIndex >= 0 && q.species.name ? (
          <>
            {feedbackText.slice(0, scientificIndex)}
            <em>{q.species.name}</em>
            {feedbackText.slice(scientificIndex + q.species.name.length)}
          </>
        ) : (
          feedbackText
        )}
      </p>
    </div>
  ) : null;

  const actionButton = (
    <div className="bioindex-quiz-answer-action">
      {answered ? (
        <button
          type="button"
          className="bioindex-quiz-primary"
          onClick={nextQuestion}
        >
          {current === questions.length - 1 ? "View Results" : "Next Question"}{" "}
          <span aria-hidden="true">→</span>
        </button>
      ) : (
        <button
          type="button"
          className="bioindex-quiz-primary"
          onClick={submitAnswer}
          disabled={selected === null}
        >
          Confirm Answer <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="bioindex-quiz-page bioindex-quiz-play">
      <BioIndexQuizNavigation sourceUID={quizOwnerUID} />
      <main className="bioindex-quiz-container">
        <div className="bioindex-quiz-session">
          <header className="bioindex-quiz-play-header">
            <div className="bioindex-quiz-play-title">
              <h1>
                Question {current + 1} <span>of {questions.length}</span>
              </h1>
              <p className="bioindex-quiz-mobile-mode-label">{quizTypeLabel}</p>
            </div>

            <div
              className="bioindex-quiz-mode"
              aria-label={`Quiz type: ${quizTypeLabel}`}
            >
              <span className="bioindex-quiz-mode-icon" aria-hidden="true">
                {isPhotoChoiceQuiz ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <circle cx="9" cy="9" r="1.4" />
                    <path d="m4.5 17 5.3-5.4 3.5 3.3 2.8-2.6 3.4 4.7" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="5.5" width="18" height="14" rx="2" />
                    <circle cx="9" cy="10" r="1.4" />
                    <path d="m4.5 17 5.3-4.4 3 2.8 2.6-2.2 4.1 3.8" />
                  </svg>
                )}
              </span>
              <strong>{quizTypeLabel}</strong>
            </div>

            <div
              className="bioindex-quiz-progress-wrap"
              aria-label={`${Math.round(progressPercent)} percent of the quiz`}
            >
              <div className="bioindex-quiz-progress-label">
                <span>Progress</span>
                <strong>{Math.round(progressPercent)}%</strong>
              </div>
              <div className="bioindex-quiz-progress-track">
                <div style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
            <div
              className="bioindex-quiz-live-score"
              aria-label={`Score ${score}`}
            >
              <strong>{score}</strong>
              <span>Score</span>
            </div>
          </header>

          <section className="bioindex-quiz-card bioindex-quiz-play-card">
            <div
              className="bioindex-quiz-question-strip"
              aria-label="Question progress"
            >
              {questions.map((question, index) => {
                const isCurrent = index === current;
                const wasAnswered = answerResults[index] !== undefined;
                const wasCorrect = answerResults[index] === true;
                return (
                  <div
                    key={`${question.photo}-${index}-progress`}
                    className={`bioindex-quiz-question-token${isCurrent ? " is-active" : ""}${wasAnswered ? (wasCorrect ? " is-correct" : " is-wrong") : ""}`}
                    aria-label={`Question ${index + 1}${wasAnswered ? (wasCorrect ? ", correct" : ", incorrect") : isCurrent ? ", current" : ""}`}
                  >
                    {index + 1}
                  </div>
                );
              })}
            </div>

            <div
              className={`bioindex-quiz-question-heading${!isPhotoChoiceQuiz ? " bioindex-quiz-text-mobile-heading" : ""}`}
            >
              <h2>{questionPrompt}</h2>
            </div>

            {isPhotoChoiceQuiz ? (
              <div className="bioindex-quiz-photo-questions">
                <div className="bioindex-quiz-identify-prompt">
                  <span>
                    {activeQuizType === "photo-from-scientific"
                      ? "Scientific name"
                      : "Common name"}
                  </span>
                  <strong
                    className={
                      activeQuizType === "photo-from-scientific"
                        ? "bioindex-quiz-scientific"
                        : ""
                    }
                  >
                    {activeQuizType === "photo-from-scientific"
                      ? q.species.name
                      : q.species.common || q.species.name}
                  </strong>
                </div>
                <div className="bioindex-quiz-photo-options">
                  {q.options.map((option, index) => {
                    const isCorrect = option === q.correct;
                    const isSelected = option === selected;
                    const letter = String.fromCharCode(65 + index);
                    return (
                      <button
                        key={`${option}-${index}`}
                        type="button"
                        onClick={() => selectAnswer(option)}
                        aria-pressed={isSelected}
                        disabled={answered}
                        className={`bioindex-quiz-photo-option${isSelected ? " is-selected" : ""}${answered && isCorrect ? " is-correct" : ""}${answered && isSelected && !isCorrect ? " is-wrong" : ""}`}
                      >
                        <span className="bioindex-quiz-answer-letter">
                          {letter}
                        </span>
                        <img src={option} alt={`Option ${letter}`} />
                        {answered &&
                          (isCorrect || (isSelected && !isCorrect)) && (
                            <span className="bioindex-quiz-answer-marker">
                              {isCorrect ? "✓" : "×"}
                            </span>
                          )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bioindex-quiz-question-workspace">
                <div className="bioindex-quiz-photo-column">
                  <div className="bioindex-quiz-question-image">
                    <img
                      src={q.photo}
                      alt="Species to identify"
                      onLoad={() => setImageLoaded(true)}
                      onError={() => setImageLoaded(true)}
                      className={imageLoaded ? "is-loaded" : ""}
                    />
                  </div>
                </div>
                <div className="bioindex-quiz-option-column">
                  <div className="bioindex-quiz-desktop-question-heading">
                    <h2>{questionPrompt}</h2>
                  </div>
                  <p className="bioindex-quiz-option-caption">
                    Choose one answer
                  </p>
                  <div className="bioindex-quiz-text-options">
                    {q.options.map((option, index) => {
                      const isCorrect = option === q.correct;
                      const isSelected = option === selected;
                      const letter = String.fromCharCode(65 + index);
                      return (
                        <button
                          key={`${option}-${index}`}
                          type="button"
                          onClick={() => selectAnswer(option)}
                          disabled={answered}
                          aria-pressed={isSelected}
                          className={`bioindex-quiz-text-option${isSelected ? " is-selected" : ""}${answered && isCorrect ? " is-correct" : ""}${answered && isSelected && !isCorrect ? " is-wrong" : ""}`}
                        >
                          <span className="bioindex-quiz-answer-letter">
                            {letter}
                          </span>
                          <span
                            className={
                              activeQuizType === "scientific-from-photo"
                                ? "bioindex-quiz-scientific"
                                : ""
                            }
                          >
                            {option}
                          </span>
                          {answered &&
                            (isCorrect || (isSelected && !isCorrect)) && (
                              <span className="bioindex-quiz-answer-marker">
                                {isCorrect ? "✓" : "×"}
                              </span>
                            )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <div className="bioindex-quiz-answer-footer">
              {feedback}
              {actionButton}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
